import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Agent, selectAgents, setAgents } from "./agentsSlice";
import { selectRepos } from "../library/librarySlice";
import { selectUser } from "../auth/authSlice";
import { apiGetAgents, apiDeleteAgent, agentWsUrl } from "../../services/AgentAPI";
import CreateAgentDialog from "./CreateAgentDialog";
import AgentModal from "./AgentModal";

interface RepoGroup {
  repoId: number;
  repoName: string;
  agents: Agent[];
}

/**
 * The Agents block shown inside the BACKGROUND panel: a "New agent" button and
 * all agents (from the backend) grouped by repo. Clicking an agent opens its
 * Claude modal, connected through the Rocket websocket proxy.
 */
export default function AgentsSection() {
  const dispatch = useAppDispatch();
  const agents = useAppSelector(selectAgents);
  const repos = useAppSelector(selectRepos);
  const user = useAppSelector(selectUser);

  const [createOpen, setCreateOpen] = useState(false);
  const [openAgent, setOpenAgent] = useState<Agent | null>(null);

  const orgId = user?.default_organization_id;
  const wsId = user?.default_workspace_id;

  const repoName = useCallback(
    (repoId: number) => repos.find((r) => r.id === repoId)?.name ?? `repo ${repoId}`,
    [repos]
  );

  const reload = useCallback(() => {
    if (orgId == null || wsId == null) return;
    apiGetAgents(orgId, wsId)
      .then((r) => dispatch(setAgents(r.data.agents ?? [])))
      .catch(() => {
        /* surfaced elsewhere; keep panel quiet */
      });
  }, [orgId, wsId, dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  const groups = useMemo<RepoGroup[]>(() => {
    const byRepo = new Map<number, RepoGroup>();
    for (const a of agents) {
      let g = byRepo.get(a.repo_id);
      if (!g) {
        g = { repoId: a.repo_id, repoName: repoName(a.repo_id), agents: [] };
        byRepo.set(a.repo_id, g);
      }
      g.agents.push(a);
    }
    return Array.from(byRepo.values()).sort((x, y) => x.repoName.localeCompare(y.repoName));
  }, [agents, repoName]);

  const handleDelete = (agentId: number) => {
    if (orgId == null || wsId == null) return;
    apiDeleteAgent(orgId, wsId, agentId).then(reload).catch(reload);
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
          Agents
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New
        </Button>
      </Box>

      {groups.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No agents yet. Create one and assign it a repo.
        </Typography>
      )}

      {groups.map((g) => (
        <List
          key={g.repoId}
          dense
          disablePadding
          subheader={
            <ListSubheader disableSticky sx={{ lineHeight: "24px", bgcolor: "transparent" }}>
              {g.repoName}
            </ListSubheader>
          }
        >
          {g.agents.map((a) => (
            <ListItem
              key={a.id}
              disablePadding
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  aria-label="delete agent"
                  onClick={() => handleDelete(a.id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemButton onClick={() => setOpenAgent(a)}>
                <ListItemText
                  primary={a.name}
                  secondary={a.status}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ))}

      <CreateAgentDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={reload} />
      {openAgent && orgId != null && wsId != null && (
        <AgentModal
          open={!!openAgent}
          onClose={() => setOpenAgent(null)}
          name={openAgent.name}
          wsUrl={agentWsUrl(orgId, wsId, openAgent.id)}
        />
      )}
    </Box>
  );
}
